import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ChevronDownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { FileUploadProgress } from '@/hooks/use-file-upload';

interface UploadProgressPanelProps {
  uploadProgress: Map<string, FileUploadProgress>;
  onClose?: () => void;
}

const STORAGE_KEY = 'uploadProgressPanel.expanded';

export function UploadProgressPanel({
  uploadProgress,
  onClose,
}: UploadProgressPanelProps) {
  const { t } = useTranslation('load');
  const [expanded, setExpanded] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Persist expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  }, [expanded]);

  const uploads = Array.from(uploadProgress.values());
  const activeUploads = uploads.filter(
    (upload) => upload.status === 'pending' || upload.status === 'uploading'
  );
  const successfulUploads = uploads.filter(
    (upload) => upload.status === 'completed' || upload.status === 'completed-with-warning'
  );
  const failedUploads = uploads.filter(
    (upload) => upload.status === 'failed' || upload.status === 'validation-failed'
  );
  const completedUploads = [...successfulUploads, ...failedUploads];

  if (uploads.length === 0) {
    return null;
  }

  const overallProgress =
    uploads.length > 0
      ? uploads.reduce((sum, upload) => sum + upload.progress, 0) / uploads.length
      : 0;

  const getStatusIcon = (status: FileUploadProgress['status']) => {
    switch (status) {
      case 'completed':
      case 'completed-with-warning':
        return <CompletedIcon color="success" fontSize="small" />;
      case 'failed':
      case 'validation-failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'uploading':
        return <UploadIcon color="primary" fontSize="small" />;
      default:
        return <UploadIcon color="disabled" fontSize="small" />;
    }
  };

  const toggleItemExpansion = (fileName: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const hasError = (upload: FileUploadProgress) => {
    return (upload.status === 'failed' || upload.status === 'validation-failed') && upload.error;
  };

  const hasWarning = (upload: FileUploadProgress) => {
    return upload.status === 'completed-with-warning' && upload.warning;
  };

  const getWarningTitle = (upload: FileUploadProgress) => {
    const warning = (upload.warning || '').toLowerCase();
    const hasFolderChange = warning.includes('folder') || warning.includes('pasta');
    const hasFileChange = warning.includes('file') || warning.includes('arquivo');

    if (hasFolderChange && hasFileChange) return t('upload.fileFolderNamesModified');
    if (hasFolderChange) return t('upload.folderNameModified');
    return t('upload.fileNameModified');
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 380,
        maxHeight: 450,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid="upload-progress-panel"
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: 'primary.dark',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
        data-testid="upload-progress-header"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <UploadIcon />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('upload.uploadingFiles')}
          </Typography>
          {activeUploads.length > 0 && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t('upload.activeCount', { count: activeUploads.length })}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {onClose && activeUploads.length === 0 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{ color: 'inherit' }}
              data-testid="upload-progress-close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {/* Overall Progress */}
          {activeUploads.length > 0 && (
            <Box sx={{ p: 2, pb: 1 }} data-testid="upload-progress-overall">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('upload.overallProgress')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('upload.progressSummary', {
                  percent: Math.round(overallProgress),
                  remaining: activeUploads.length,
                  total: uploads.length,
                })}
              </Typography>
            </Box>
          )}

          {/* Upload List */}
          <List dense sx={{ py: 0 }} data-testid="upload-progress-list">
            {uploads.map((upload, index) => {
              const isExpanded = expandedItems.has(upload.fileName);
              const showExpandButton = hasError(upload) || hasWarning(upload);
              
              return (
                <Box key={upload.fileName}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      borderBottom: index < uploads.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      cursor: showExpandButton ? 'pointer' : 'default',
                      '&:hover': showExpandButton ? {
                        backgroundColor: 'action.hover',
                      } : {},
                    }}
                    onClick={() => showExpandButton && toggleItemExpansion(upload.fileName)}
                    data-testid={`upload-progress-item-${index}`}
                  >
                    {showExpandButton && (
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {isExpanded ? (
                          <ChevronDownIcon fontSize="small" color="action" />
                        ) : (
                          <ChevronRightIcon fontSize="small" color="action" />
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemIcon sx={{ minWidth: 36, ml: showExpandButton ? 0 : 3 }}>
                      {getStatusIcon(upload.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: showExpandButton ? '200px' : '280px',
                            }}
                            title={upload.fileName}
                          >
                            {upload.fileName}
                          </Typography>
                          {hasWarning(upload) && (
                            <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {upload.status === 'uploading' && (
                            <>
                              <LinearProgress
                                variant="determinate"
                                value={upload.progress}
                                color="primary"
                                sx={{ mb: 0.5, height: 4, borderRadius: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {Math.round(upload.progress)}%
                              </Typography>
                            </>
                          )}
                          {upload.status === 'completed' && (
                            <Typography variant="caption" color="success.main">
                              {t('upload.uploadComplete')}
                            </Typography>
                          )}
                          {upload.status === 'completed-with-warning' && !isExpanded && (
                            <Typography variant="caption" color="warning.main">
                              {t('upload.uploadedWithModification')}
                            </Typography>
                          )}
                          {upload.status === 'failed' && !isExpanded && (
                            <Typography
                              variant="caption"
                              color="error.main"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {upload.error || t('upload.uploadFailed')}
                            </Typography>
                          )}
                          {upload.status === 'validation-failed' && !isExpanded && (
                            <Typography
                              variant="caption"
                              color="error.main"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t('upload.invalid', { error: upload.error || t('upload.fileNameNotAllowed') })}
                            </Typography>
                          )}
                          {upload.status === 'pending' && (
                            <Typography variant="caption" color="text.secondary">
                              {t('upload.waitingToStart')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  
                  {/* Expanded error/warning details */}
                  {showExpandButton && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          px: 3,
                          py: 2,
                          ml: 6,
                          borderLeft: 2,
                          borderColor: hasError(upload) ? 'error.main' : 'warning.main',
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          fontWeight="bold" 
                          color={hasError(upload) ? 'error.main' : 'warning.main'} 
                          display="block" 
                          gutterBottom
                        >
                          {hasError(upload)
                            ? upload.status === 'validation-failed'
                              ? t('upload.validationError')
                              : t('upload.uploadError')
                            : getWarningTitle(upload)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color={hasError(upload) ? 'error.dark' : 'warning.dark'}
                          sx={{ 
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {upload.error || upload.warning}
                        </Typography>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>

          {/* Summary */}
          {completedUploads.length > 0 && activeUploads.length === 0 && (
            <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CompletedIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('upload.succeeded', { count: successfulUploads.length })}
                  </Typography>
                </Stack>
                {failedUploads.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="caption">
                      {t('upload.failed', { count: failedUploads.length })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
